import { ChevronDown, ChevronRight, ExternalLink, Maximize, Minus, Plus, Sparkles } from 'lucide-react'
import { type PointerEvent as RPointerEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'wouter'
import { api } from '../lib/api'
import { fmt, pct } from '../lib/format'
import type { ExplainNode, TreeExhibit, TreeNode } from '../lib/types'
import { useAgent, useWorkspace } from './Shell'
import { Formula, Spinner } from './ui'

const KIND: Record<TreeNode['kind'], { label: string; color: string }> = {
  input: { label: 'Input', color: 'var(--amber)' },
  calc: { label: 'Formula', color: 'var(--data)' },
  actual: { label: 'Actual', color: 'var(--green)' },
}
const OP: Record<NonNullable<TreeNode['combine']>, string> = { sum: '+', product: '×', mixed: 'ƒ' }

/** A driver-tree node from a raw explanation (the agent's data_explain), for previews. */
export function explainToTree(n: ExplainNode, id = '0'): TreeNode {
  const format = n.unit === 'USD' ? 'usd' : n.unit === 'FTE' ? 'fte' : /percent|%/i.test(n.unit) ? 'percent' : 'number'
  return {
    id,
    measure: n.measure,
    label: n.label,
    kind: n.kind,
    format,
    unit: n.unit,
    value: n.value,
    base: null,
    coord: n.coord,
    coordLabels: n.coordLabels,
    formula: n.formula,
    notes: n.notes,
    combine: n.combine ?? null,
    truncated: n.truncated ?? false,
    children: n.children.map((c, i) => explainToTree(c, `${id}.${i}`)),
  }
}

export const delta = (n: TreeNode) => (n.base === null ? null : n.value - n.base)
export const share = (n: TreeNode, parent: TreeNode | null) => (parent?.combine === 'sum' && parent.value ? n.value / parent.value : null)

export function viewHref(measure: string, coord: Record<string, string>, version?: string, compare?: string | null) {
  const p = new URLSearchParams({ measure })
  for (const [l, v] of Object.entries(coord)) p.append('f', `${l}:${v}`)
  if (version) p.set('version', version)
  if (compare) p.set('compare', compare)
  return `/views?${p}`
}

function DeltaText({
  n,
  className = '',
  quiet = false,
  good,
}: {
  n: TreeNode
  className?: string
  quiet?: boolean
  good?: 'up' | 'down' | null
}) {
  const d = delta(n)
  if (d === null || Math.abs(d) < 0.5)
    return n.base !== null && !quiet ? <span className={`tree-delta flat ${className}`}>no change</span> : null
  const p = n.base ? d / Math.abs(n.base) : null
  return (
    <span className={`tree-delta ${good ? ((good === 'up' ? d > 0 : d < 0) ? 'good' : 'bad') : d > 0 ? 'up' : 'down'} ${className}`}>
      {d > 0 ? '▲' : '▼'} {fmt(d, n.format, true)}
      {p !== null && Math.abs(p) >= 0.0005 ? ` (${pct(p, 1, true)})` : ''}
    </span>
  )
}

// ---------------------------------------------------------------- compact tree (the panel)

/** The tree as indented rows: value, change and share of the parent at each node. */
export function CompactTree({ root, maxDepth = 1, good }: { root: TreeNode; maxDepth?: number; good?: 'up' | 'down' | null }) {
  return (
    <div className="ctree">
      <CompactNode node={root} parent={null} depth={0} maxDepth={maxDepth} good={good} />
    </div>
  )
}

function CompactNode({
  node,
  parent,
  depth,
  maxDepth,
  good,
}: {
  node: TreeNode
  parent: TreeNode | null
  depth: number
  maxDepth: number
  good?: 'up' | 'down' | null
}) {
  // a single input (an aggregation passing through) unfolds on its own, so the first view shows real drivers
  const [open, setOpen] = useState(depth < maxDepth)
  const has = node.children.length > 0
  const s = share(node, parent)
  const d = delta(node)
  const moved = d !== null && Math.abs(d) >= 0.5
  const partial = s !== null && s < 0.999
  return (
    <div>
      <button
        type="button"
        aria-expanded={has ? open : undefined}
        className={`ctree-row ${depth === 0 ? 'root' : ''}`}
        style={{ paddingLeft: 6 + depth * 16 }}
        onClick={() => has && setOpen(!open)}
        title={node.formula ?? undefined}
      >
        <span className="ctree-caret">{has ? open ? <ChevronDown size={12} /> : <ChevronRight size={12} /> : null}</span>
        {parent?.combine && depth > 0 && <span className="ctree-op">{OP[parent.combine]}</span>}
        <span className="ctree-kind" style={{ background: KIND[node.kind].color }} title={KIND[node.kind].label} />
        <span className="ctree-label">{node.label}</span>
        <span className="ctree-value num">{fmt(node.value, node.format)}</span>
        {partial && s !== null && <span className="ctree-share num">{pct(s, 0)}</span>}
      </button>
      {(moved || partial) && (
        <div className="ctree-sub" style={{ paddingLeft: 6 + depth * 16 + 34 }}>
          {partial && s !== null && (
            <span className="ctree-bar">
              <span style={{ width: `${Math.max(1, Math.min(100, Math.abs(s) * 100))}%` }} />
            </span>
          )}
          <DeltaText n={node} quiet good={good} />
        </div>
      )}
      {open &&
        node.children.map(c => (
          // a lone input (an aggregation passing through) doesn't use up a level
          <CompactNode
            key={c.id}
            node={c}
            parent={node}
            depth={depth + 1}
            maxDepth={node.children.length === 1 ? maxDepth + 1 : maxDepth}
            good={good}
          />
        ))}
    </div>
  )
}

// ---------------------------------------------------------------- diagram (expanded)

const NW = 236
const NH = 78
const GX = 72
const GY = 14

type Laid = { node: TreeNode; parent: Laid | null; x: number; y: number; children: Laid[] }

function layout(root: TreeNode, collapsed: Set<string>) {
  let cursor = 0
  const all: Laid[] = []
  const place = (n: TreeNode, parent: Laid | null, depth: number): Laid => {
    const me: Laid = { node: n, parent, x: depth * (NW + GX), y: 0, children: [] }
    all.push(me)
    const kids = collapsed.has(n.id) ? [] : n.children
    if (!kids.length) {
      me.y = cursor
      cursor += NH + GY
    } else {
      me.children = kids.map(k => place(k, me, depth + 1))
      me.y = ((me.children[0] as Laid).y + (me.children.at(-1) as Laid).y) / 2
    }
    return me
  }
  place(root, null, 0)
  const width = Math.max(...all.map(l => l.x)) + NW
  const height = Math.max(NH, cursor - GY)
  return { all, width, height }
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

/**
 * The driver tree as a diagram, left to right: the number, the formula's inputs, their
 * drivers. Drag to pan, pinch or ⌘-scroll to zoom. Click a node to inspect it; collapse and
 * expand branches; branches cut off by depth load on demand.
 */
export function DriverDiagram({ exhibit }: { exhibit: TreeExhibit }) {
  const { wid } = useWorkspace()
  const { ask } = useAgent()
  const [root, setRoot] = useState<TreeNode>(exhibit.root)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    // open two levels; deeper branches start folded so the first view reads at a glance
    const c = new Set<string>()
    const walk = (n: TreeNode, d: number) => {
      if (d >= 2 && n.children.length) c.add(n.id)
      for (const k of n.children) walk(k, d + 1)
    }
    walk(exhibit.root, 0)
    return c
  })
  const [selected, setSelected] = useState<string>(exhibit.root.id)
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState({ k: 1, tx: 24, ty: 24 })
  const [reveal, setReveal] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean } | null>(null)
  const { all, width, height } = useMemo(() => layout(root, collapsed), [root, collapsed])
  const byId = useMemo(() => new Map(all.map(l => [l.node.id, l])), [all])
  const sel = byId.get(selected) ?? all[0]

  // after a branch opens, bring it into view: pan, and zoom out if it can't fit
  useLayoutEffect(() => {
    if (!reveal) return
    const l = byId.get(reveal)
    const el = box.current
    setReveal(null)
    if (!l || !el) return
    const nodes = [l, ...l.children]
    const x0 = Math.min(...nodes.map(n => n.x))
    const x1 = Math.max(...nodes.map(n => n.x)) + NW + 24
    const y0 = Math.min(...nodes.map(n => n.y))
    const y1 = Math.max(...nodes.map(n => n.y)) + NH
    const r = el.getBoundingClientRect()
    const m = 24
    setView(v => {
      // zoom out only as far as the branch needs, then shift it inside the margins as little as possible
      const k = Math.max(0.25, Math.min(v.k, (r.width - 2 * m) / (x1 - x0), (r.height - 2 * m) / (y1 - y0)))
      let tx = v.k === k ? v.tx : r.width / 2 - ((x0 + x1) / 2) * k
      let ty = v.k === k ? v.ty : r.height / 2 - ((y0 + y1) / 2) * k
      if (x1 * k + tx > r.width - m) tx = r.width - m - x1 * k
      if (x0 * k + tx < m) tx = m - x0 * k
      if (y1 * k + ty > r.height - m) ty = r.height - m - y1 * k
      if (y0 * k + ty < m) ty = m - y0 * k
      return { k, tx, ty }
    })
  }, [reveal, byId])

  const fit = useCallback(() => {
    const el = box.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const k = Math.min(1.15, Math.max(0.3, Math.min((r.width - 48) / width, (r.height - 48) / height)))
    setView({ k, tx: (r.width - width * k) / 2, ty: Math.max(24, (r.height - height * k) / 2) })
  }, [width, height])
  // biome-ignore lint/correctness/useExhaustiveDependencies: fit once when the diagram first lays out
  useLayoutEffect(() => {
    fit()
  }, [])

  const zoomAt = (factor: number, cx?: number, cy?: number) => {
    const el = box.current
    const r = el?.getBoundingClientRect()
    const px = cx ?? (r ? r.width / 2 : 0)
    const py = cy ?? (r ? r.height / 2 : 0)
    setView(v => {
      const k = Math.min(2.5, Math.max(0.25, v.k * factor))
      return { k, tx: px - ((px - v.tx) * k) / v.k, ty: py - ((py - v.ty) * k) / v.k }
    })
  }
  useEffect(() => {
    const el = box.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      if (e.ctrlKey || e.metaKey) zoomAt(Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top)
      else setView(v => ({ ...v, tx: v.tx - e.deltaX, ty: v.ty - e.deltaY }))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  })

  const onDown = (e: RPointerEvent) => {
    if ((e.target as Element).closest('[data-node]')) return
    drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty, moved: false }
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  }
  const onMove = (e: RPointerEvent) => {
    const d = drag.current
    if (!d) return
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) d.moved = true
    setView(v => ({ ...v, tx: d.tx + e.clientX - d.x, ty: d.ty + e.clientY - d.y }))
  }
  const onUp = () => {
    drag.current = null
  }

  const toggle = async (n: TreeNode) => {
    setSelected(n.id)
    if (n.truncated && !n.children.length) {
      setLoading(n.id)
      setError(null)
      try {
        const p = new URLSearchParams({ measure: n.measure, coord: JSON.stringify(n.coord), version: exhibit.version.key, depth: '3' })
        if (exhibit.compare) p.set('compare', exhibit.compare.key)
        const more = await api.get<TreeExhibit>(`/w/${wid}/explain/tree?${p}`)
        const reid = (x: TreeNode, id: string): TreeNode => ({ ...x, id, children: x.children.map((c, i) => reid(c, `${id}.${i}`)) })
        const graft = (x: TreeNode): TreeNode =>
          x.id === n.id
            ? { ...x, truncated: false, children: more.root.children.map((c, i) => reid(c, `${n.id}.${i}`)) }
            : { ...x, children: x.children.map(graft) }
        setRoot(r => graft(r))
        setCollapsed(c => {
          const next = new Set(c)
          next.delete(n.id)
          return next
        })
        setReveal(n.id)
      } catch (e) {
        setError((e as Error).message)
      } finally {
        setLoading(null)
      }
      return
    }
    const opening = collapsed.has(n.id)
    setCollapsed(c => {
      const next = new Set(c)
      if (next.has(n.id)) next.delete(n.id)
      else next.add(n.id)
      return next
    })
    if (opening) setReveal(n.id)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (!sel) return
    const siblings = sel.parent?.children ?? [sel]
    const i = siblings.indexOf(sel)
    const go = (l: Laid | undefined) => l && setSelected(l.node.id)
    if (e.key === 'ArrowRight') {
      if (collapsed.has(sel.node.id) || (sel.node.truncated && !sel.node.children.length)) void toggle(sel.node)
      else go(sel.children[0])
    } else if (e.key === 'ArrowLeft') go(sel.parent ?? undefined)
    else if (e.key === 'ArrowDown') go(siblings[i + 1])
    else if (e.key === 'ArrowUp') go(siblings[i - 1])
    else if (e.key === 'Enter' || e.key === ' ') void toggle(sel.node)
    else if (e.key === '+' || e.key === '=') zoomAt(1.2)
    else if (e.key === '-') zoomAt(1 / 1.2)
    else if (e.key === '0') fit()
    else return
    e.preventDefault()
  }

  const pShare = sel ? share(sel.node, sel.parent?.node ?? null) : null
  return (
    <div className="diagram">
      <div
        className="diagram-canvas"
        ref={box}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the canvas is a keyboard-driven widget (arrows move between nodes, Enter expands)
        tabIndex={0}
        role="application"
        aria-label="Driver tree diagram. Arrow keys move between nodes, Enter expands or collapses."
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onKeyDown={onKey}
      >
        <svg width="100%" height="100%">
          <title>{`Driver tree: ${root.label}`}</title>
          <defs>
            <pattern id="dots" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.8" fill="var(--border-strong)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
          <g transform={`translate(${view.tx},${view.ty}) scale(${view.k})`}>
            {all.map(l =>
              l.parent ? (
                <g key={`e-${l.node.id}`}>
                  <path
                    d={`M${l.parent.x + NW},${l.parent.y + NH / 2} C${l.parent.x + NW + GX / 2},${l.parent.y + NH / 2} ${l.x - GX / 2},${l.y + NH / 2} ${l.x},${l.y + NH / 2}`}
                    fill="none"
                    stroke={l.node.id === selected || l.parent.node.id === selected ? 'var(--text)' : 'var(--border-strong)'}
                    strokeWidth={1}
                  />
                  {l.parent.node.combine && (
                    <g transform={`translate(${l.x - 11},${l.y + NH / 2})`}>
                      <rect x={-8} y={-8} width={16} height={16} fill="var(--surface)" stroke="var(--border-strong)" />
                      <text textAnchor="middle" y={4} className="diagram-op">
                        {OP[l.parent.node.combine]}
                      </text>
                    </g>
                  )}
                </g>
              ) : null,
            )}
            {all.map(l => {
              const n = l.node
              const s = share(n, l.parent?.node ?? null)
              const d = delta(n)
              const isSel = n.id === selected
              const canOpen = n.children.length > 0 || n.truncated
              const folded = collapsed.has(n.id) || (n.truncated && !n.children.length)
              return (
                <g
                  key={n.id}
                  data-node
                  transform={`translate(${l.x},${l.y})`}
                  onClick={() => setSelected(n.id)}
                  onDoubleClick={() => canOpen && void toggle(n)}
                  className="diagram-node"
                >
                  <rect
                    width={NW}
                    height={NH}
                    fill="var(--surface)"
                    stroke={isSel ? 'var(--text)' : 'var(--border-strong)'}
                    strokeWidth={isSel ? 1.5 : 1}
                  />
                  <rect width={3} height={NH} fill={KIND[n.kind].color} />
                  <text x={14} y={20} className="diagram-label">
                    {clip(n.label, 26)}
                  </text>
                  <text x={NW - 10} y={20} textAnchor="end" className="diagram-kind">
                    {KIND[n.kind].label}
                  </text>
                  <text x={14} y={44} className="diagram-value">
                    {fmt(n.value, n.format)}
                  </text>
                  {d !== null && Math.abs(d) >= 0.5 && (
                    <text x={NW - 10} y={44} textAnchor="end" className={`diagram-delta ${d > 0 ? 'up' : 'down'}`}>
                      {d > 0 ? '▲' : '▼'} {fmt(d, n.format, true)}
                    </text>
                  )}
                  {s !== null ? (
                    <g>
                      <rect x={14} y={60} width={NW - 118} height={4} fill="var(--surface-3)" />
                      <rect x={14} y={60} width={Math.max(1, (NW - 118) * Math.min(1, Math.abs(s)))} height={4} fill="var(--data)" />
                      <text x={NW - 10} y={65} textAnchor="end" className="diagram-meta">
                        {pct(s, 0)} of parent
                      </text>
                    </g>
                  ) : (
                    <text x={14} y={66} className="diagram-meta">
                      {clip(Object.values(n.coordLabels).join(' · ') || n.unit, 34)}
                    </text>
                  )}
                  {canOpen && (
                    <g
                      transform={`translate(${NW},${NH / 2})`}
                      onClick={e => {
                        e.stopPropagation()
                        void toggle(n)
                      }}
                      className="diagram-toggle"
                    >
                      <rect x={-1} y={-9} width={18} height={18} fill="var(--surface)" stroke="var(--border-strong)" />
                      {loading === n.id ? (
                        <text x={8} y={4} textAnchor="middle" className="diagram-op">
                          …
                        </text>
                      ) : (
                        <text x={8} y={4.5} textAnchor="middle" className="diagram-op">
                          {folded ? '+' : '−'}
                        </text>
                      )}
                    </g>
                  )}
                </g>
              )
            })}
          </g>
        </svg>
        <div className="diagram-tools">
          <button type="button" onClick={() => zoomAt(1.2)} aria-label="Zoom in" title="Zoom in (+)">
            <Plus size={14} />
          </button>
          <button type="button" onClick={() => zoomAt(1 / 1.2)} aria-label="Zoom out" title="Zoom out (−)">
            <Minus size={14} />
          </button>
          <button type="button" onClick={fit} aria-label="Fit to screen" title="Fit (0)">
            <Maximize size={13} />
          </button>
        </div>
        <div className="diagram-legend">
          {Object.values(KIND).map(k => (
            <span key={k.label}>
              <span className="sq" style={{ background: k.color }} />
              {k.label}
            </span>
          ))}
          <span>
            <span className="op">+</span> adds up · <span className="op">×</span> multiplies
          </span>
        </div>
        {error && <div className="diagram-error">{error}</div>}
      </div>
      {sel && (
        <aside className="inspector" aria-live="polite">
          <div className="eyebrow" style={{ color: KIND[sel.node.kind].color }}>
            {KIND[sel.node.kind].label}
          </div>
          <h3 className="inspector-title">{sel.node.label}</h3>
          <div className="inspector-coord">
            {Object.values(sel.node.coordLabels).map(c => (
              <span key={c} className="pill">
                {c}
              </span>
            ))}
          </div>
          <dl className="inspector-figures">
            <dt>{exhibit.version.label}</dt>
            <dd className="num">{fmt(sel.node.value, sel.node.format)}</dd>
            {exhibit.compare && (
              <>
                <dt>{exhibit.compare.label}</dt>
                <dd className="num">{sel.node.base === null ? '—' : fmt(sel.node.base, sel.node.format)}</dd>
                <dt>Change</dt>
                <dd>
                  <DeltaText n={sel.node} />
                </dd>
              </>
            )}
            {pShare !== null && (
              <>
                <dt>Share of {sel.parent?.node.label}</dt>
                <dd className="num">{pct(pShare, 1)}</dd>
              </>
            )}
          </dl>
          {sel.node.formula && (
            <div className="inspector-block">
              <div className="eyebrow">Formula</div>
              <Formula text={sel.node.formula} />
            </div>
          )}
          {sel.node.kind === 'input' && (
            <div className="inspector-block small muted">An input: entered by owners, imported, or proposed through a changeset.</div>
          )}
          {sel.node.notes.length > 0 && (
            <div className="inspector-block tiny muted">
              {sel.node.notes.map(n => (
                <div key={n}>{n}</div>
              ))}
            </div>
          )}
          <div className="inspector-actions">
            <Link href={viewHref(sel.node.measure, sel.node.coord, exhibit.version.key, exhibit.compare?.key)} className="btn sm">
              <ExternalLink size={12} /> Open in view
            </Link>
            <button
              type="button"
              className="btn sm ghost"
              onClick={() =>
                ask(
                  `Why is ${sel.node.label} ${fmt(sel.node.value, sel.node.format)} at ${Object.values(sel.node.coordLabels).join(', ') || 'this slice'}${exhibit.compare ? ` versus ${exhibit.compare.label}` : ''}? What drives it?`,
                  `${sel.node.measure} @ ${JSON.stringify(sel.node.coord)}`,
                )
              }
            >
              <Sparkles size={12} /> Ask about this
            </button>
          </div>
          {loading && (
            <div className="tiny faint row" style={{ marginTop: 10 }}>
              <Spinner size={10} /> loading drivers…
            </div>
          )}
        </aside>
      )}
    </div>
  )
}
